import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { Lock, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BG = "https://images.unsplash.com/photo-1765405016584-0ad9f20e2ff9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NTYxOTB8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc3RvcmFnZSUyMHRhbmslMjBmYWNpbGl0eXxlbnwwfHx8fDE3Njk3ODc1OTl8MA&ixlib=rb-4.1.0&q=85";

const Login = ({ setIsAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(DEFAULT_BG);
  const navigate = useNavigate();
  const { t, isRtl, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    fetchBackgroundImage();
  }, []);

  const fetchBackgroundImage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/login-background`);
      if (response.data.url) {
        setBackgroundImage(`${BACKEND_URL}${response.data.url}`);
      }
    } catch (error) {
      // Use default background if fetch fails
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        username,
        password,
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.role);
      setIsAuthenticated(true);
      toast.success(t.loginSuccess);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex bg-white ${isRtl ? 'flex-row-reverse' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Language Toggle - Fixed Position */}
      <button
        onClick={toggleLanguage}
        className={`fixed top-4 ${isRtl ? 'left-4' : 'right-4'} z-50 flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm text-slate-800 rounded-lg shadow-lg hover:bg-white transition-all border border-slate-200`}
      >
        <Globe className="w-5 h-5 text-blue-600" />
        <span className="font-medium">{language === 'en' ? 'العربية' : 'English'}</span>
      </button>

      <div className={`hidden lg:flex lg:w-1/2 relative overflow-hidden ${isRtl ? 'order-2' : ''}`}>
        <img
          src={backgroundImage}
          alt="Industrial tanks"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 to-slate-900/40 flex items-center justify-center">
          <div className={`text-white text-center px-12 ${isRtl ? 'text-right' : ''}`}>
            <h1 className="text-5xl font-bold mb-4">TankGuard Enterprise</h1>
            <p className="text-xl text-slate-200">
              {isRtl ? 'نظام إدارة المستندات ورموز QR الآمن' : 'Secure Document & QR Management System'}
            </p>
          </div>
        </div>
      </div>

      <div className={`w-full lg:w-1/2 flex items-center justify-center p-8 bg-white ${isRtl ? 'order-1' : ''}`}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
            <div className="flex items-center justify-center w-16 h-16 bg-slate-900 rounded-lg mb-6 mx-auto">
              <Lock className="w-8 h-8 text-white" />
            </div>

            <h2 className={`text-3xl font-bold text-center mb-2 text-slate-900 ${isRtl ? '' : ''}`}>{t.adminLogin}</h2>
            <p className={`text-center text-slate-600 mb-8 ${isRtl ? '' : ''}`}>{t.enterCredentials}</p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="username" className={`text-slate-900 font-medium ${isRtl ? 'block text-right' : ''}`}>{t.username}</Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t.enterUsername}
                  required
                  className={`mt-2 h-11 ${isRtl ? 'text-right' : ''}`}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </div>

              <div>
                <Label htmlFor="password" className={`text-slate-900 font-medium ${isRtl ? 'block text-right' : ''}`}>{t.password}</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.enterPassword}
                  required
                  className={`mt-2 h-11 ${isRtl ? 'text-right' : ''}`}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                disabled={loading}
              >
                {loading ? t.loggingIn : t.login}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
